import React, { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { getNurseProfileRequest, updateNursesRequest } from "../redux/actions";
import { editHeadNurseProfileRequest, getNurseByHNIdRequest, getSeniorsByHNRequest } from "../redux/actions/headNurse";
import { AppState } from "../redux/types";
import { getDefaultOptions, getOptions } from "../utils/autocomplete";
import { ROLE } from "../utils/constants";
import AutoComplete from "./Autocomplete";
import FormContainer from "./FormContainer";
import Loader from "./Loader";
import Message from "./Message";
import { localePath } from "../i18n/config";

const NurseEditForm = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();

    const user = useSelector((state: AppState) => state.user);
    const nurses: any = useSelector((state: any) => state.nurses);
    const headNurse = useSelector((state: AppState) => state.headNurse);
    const seniorHomes = useSelector((state: AppState) => state.seniorHomes.list);
    const seniorsList = useSelector((state: AppState) => state.headNurse.seniors?.list);
    const filteredHomes = seniorHomes;

    // Determine which data source to use based on user role
    const isHeadNurse = user.role === ROLE.HEAD_NURSE;
    const currentState = isHeadNurse ? headNurse.nurse : nurses;

    const { error, loading } = currentState;
    const nurseProfile = isHeadNurse ? headNurse.nurse.data : nurses.profile;

    const [formData, setFormData] = useState({
        id: "",
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        updatedAt: "",
        seniors: [],
        seniorHomeIds: user.role === ROLE.HEAD_NURSE ? user.supportStaff?.seniorHomeIds : "",
    });
    const [isDisabled, setIsDisabled] = useState(true);

    useEffect(() => {
        if (isHeadNurse) {
            dispatch(getNurseByHNIdRequest(Number(id)));
            dispatch(getSeniorsByHNRequest());
        } else {
            dispatch(getNurseProfileRequest(id));
        }
    }, [dispatch, id, isHeadNurse]);

    const seniorsData = useMemo(() => {
        if (isHeadNurse && seniorsList?.length > 0) {
            return seniorsList && nurseProfile && getDefaultOptions(seniorsList, nurseProfile.seniors);
        }
        return nurses.seniorList && nurseProfile && getDefaultOptions(nurses.seniorList, nurseProfile.seniors);
    }, [isHeadNurse, seniorsList, nurseProfile, nurses]);

    useEffect(() => {
        if (user && (user.isAdmin || user.role === "it_support" || user.role === ROLE.HEAD_NURSE)) {
            const _seniors = seniorsData;

            setFormData((prevValue: any) => {
                return {
                    ...prevValue,
                    id: nurseProfile?.id || "",
                    firstName: nurseProfile?.firstName || "",
                    lastName: nurseProfile?.lastName || "",
                    email: nurseProfile?.email || "",
                    mobileNumber: nurseProfile?.mobileNumber || "",
                    updatedAt: nurseProfile?.updatedAt || "",
                    seniorHomeIds: String(nurseProfile?.seniorHomeIds) || "",
                    seniors: _seniors || [],
                };
            });
        } else {
            history.push(localePath("kirjaudu"));
        }
    }, [history, user, nurseProfile, seniorsData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name } = e.target;

        setIsDisabled(false);
        setFormData((prevValue: any) => {
            return {
                ...prevValue,
                [name]: value,
            };
        });
    };

    const handleAssignedSenior = (seniors: any) => {
        setIsDisabled(false);
        setFormData((prevValue: any) => {
            return {
                ...prevValue,
                seniors: seniors,
            };
        });
    };

    const submitHandler = (e: React.FormEvent) => {
        e.preventDefault();
        let nurseData = {
            id: formData.id,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            mobileNumber: formData.mobileNumber,
            seniorHomeIds: formData.seniorHomeIds === "" ? null : Number(formData.seniorHomeIds),
            seniors: formData.seniors.map((senior: any) => senior.value),
        };
        if (nurseProfile.role === ROLE.HEAD_NURSE) {
            const headNurseData = {
                id: formData.id,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                mobileNumber: formData.mobileNumber,
                seniorHomeIds: formData.seniorHomeIds === "" ? null : Number(formData.seniorHomeIds),
            };
            dispatch(editHeadNurseProfileRequest(headNurseData, history));
        } else {
            dispatch(updateNursesRequest({ nurseData: nurseData, history: history }));
        }
    };

    const isFieldDisabled = user.isAdmin || user.role === ROLE.HEAD_NURSE ? false : true;
    return (
        <FormContainer>
            <h1 className="user-form-header">Edit Nurse Profile</h1>
            {loading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error.message || error}</Message>
            ) : (
                <Form onSubmit={submitHandler} className="user-form">
                    <Form.Group controlId="firstname-nurse">
                        <Form.Label>*First Name</Form.Label>
                        <Form.Control type="text" placeholder="First Name" name="firstName" value={formData.firstName} onChange={handleChange} disabled={isFieldDisabled} />
                    </Form.Group>

                    <Form.Group controlId="lastname-nurse">
                        <Form.Label>*Last Name</Form.Label>
                        <Form.Control type="text" placeholder="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} disabled={isFieldDisabled} />
                    </Form.Group>

                    <Form.Group controlId="email-nurse">
                        <Form.Label>*Email</Form.Label>
                        <Form.Control type="email" placeholder="Email" name="email" value={formData.email} onChange={handleChange} disabled={isFieldDisabled} />
                    </Form.Group>

                    <Form.Group controlId="mobileNumber-nurse">
                        <Form.Label>*Phone Number</Form.Label>
                        <Form.Control type="number" placeholder="0" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} disabled={isFieldDisabled} />
                    </Form.Group>

                    {nurseProfile?.role !== ROLE.HEAD_NURSE && (
                        <Form.Group controlId="assignedSenior-nurse">
                            <Form.Label>Senior Assigned</Form.Label>
                            <AutoComplete
                                options={getOptions(isHeadNurse ? seniorsList : nurses.seniorList, formData.seniors)}
                                value={formData.seniors}
                                handleOptions={handleAssignedSenior}
                                placeholder={"Enter Senior Name/ID"}
                                isDisabled={isFieldDisabled}
                            />
                        </Form.Group>
                    )}

                    <Form.Group controlId="seniorHome">
                        <Form.Label>Yritys</Form.Label>
                        <Form.Control as="select" name="seniorHomeIds" onChange={handleChange} value={String(formData.seniorHomeIds)} disabled={isFieldDisabled || user.role === ROLE.HEAD_NURSE}>
                            <option value="">Valitse Yritys</option>
                            {filteredHomes.map((seniorHome) => {
                                return (
                                    <option value={seniorHome.id} key={seniorHome.id}>
                                        {seniorHome.name}
                                    </option>
                                );
                            })}
                        </Form.Control>
                    </Form.Group>

                    {(user.isAdmin || user.role === ROLE.HEAD_NURSE) && (
                        <Button style={{ float: "right" }} type="submit" className="tallenna user-form-button" disabled={isDisabled}>
                            Submit
                        </Button>
                    )}
                </Form>
            )}
        </FormContainer>
    );
};
export default NurseEditForm;
